import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98]',
  {
    variants: {
      variant: {
        primary:
          'bg-gradient-to-b from-primary-400 to-primary-500 text-primary-foreground shadow-sm hover:shadow-md hover:-translate-y-0.5',
        secondary:
          'border border-border bg-card text-foreground shadow-xs hover:bg-muted',
        accent:
          'bg-secondary-500 text-white shadow-sm hover:bg-secondary-400 hover:shadow-md hover:-translate-y-0.5',
        ghost: 'hover:bg-muted text-foreground',
        danger: 'bg-error text-white shadow-sm hover:bg-error/90',
        outline: 'border border-border bg-transparent hover:bg-muted',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-9 px-3 text-xs rounded-sm',
        md: 'min-h-12 h-12 px-4 py-2',
        lg: 'h-12 px-6 text-base rounded-lg',
        icon: 'h-12 w-12 min-h-12 min-w-12',
        'icon-lg': 'h-12 w-12 min-h-12 min-w-12',
        scorer: 'h-16 min-w-16 px-6 text-lg rounded-lg',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
);

const Button = React.forwardRef(
  ({ className, variant, size, asChild = false, loading = false, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        {children}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
